import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';

import { SelectItem } from '../../../../../shared/models/select-item.model';
import { ServiceRequestListFilter } from './service-request-list-filter.model';

@Component({
  selector: 'carehub-service-request-list-filter',
  standalone: true,
  imports: [
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    ReactiveFormsModule
],
  templateUrl: './service-request-list-filter.component.html',
  styleUrl: './service-request-list-filter.component.scss',
})
export class ServiceRequestListFilterComponent implements OnInit {
  @Input() filterParams: HttpParams = new HttpParams();
  @Output() filterChange = new EventEmitter<HttpParams>();

  form!: FormGroup;

  statusOptions: SelectItem[] = [
    { name: 'New', value: 'New' },
    { name: 'Paused', value: 'Paused' },
    { name: 'Approved', value: 'Approved' },
    { name: 'Contested', value: 'Contested' },
    { name: 'Awaiting user', value: 'Awaiting user' },
    { name: 'In progress', value: 'In progress' },
    { name: 'Committed', value: 'Committed' },
    { name: 'Completed', value: 'Completed' },
    { name: 'Closed', value: 'Closed' },
    { name: 'Blocked/Awaiting', value: 'Blocked/Awaiting' },
  ];

  levelOptions: SelectItem[] = [
    {
      name: $localize`:@@common.low:Low`,
      value: '4',
    },
    {
      name: $localize`:@@common.medium:Medium`,
      value: '3',
    },
    {
      name: $localize`:@@common.high:High`,
      value: '2',
    },
    {
      name: $localize`:@@common.critical:Critical`,
      value: '1',
    },
  ];

  criticalOptions: SelectItem[] = [
    { name: $localize`:@@common.yes:Yes`, value: 'True' },
    { name: $localize`:@@common.no:No`, value: 'False' },
  ];

  private initialStatusValues: SelectItem[] = [
    { name: 'New', value: 'New' },
    { name: 'Paused', value: 'Paused' },
    { name: 'Approved', value: 'Approved' },
    { name: 'Contested', value: 'Contested' },
    { name: 'Awaiting user', value: 'Awaiting user' },
    { name: 'In progress', value: 'In progress' },
    { name: 'Committed', value: 'Committed' },
    { name: 'Blocked/Awaiting', value: 'Blocked/Awaiting' },
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      searchTerm: [''],
      status: [this.initialStatusValues],
      requester: [''],
      severity: [null],
      urgency: [null],
      critical: [null],
    });
  }

  /**
   * Update filter based on form values and emit HTTP params
   */
  filter(): void {
    const httpParams: HttpParams = this.buildHttpParams();
    this.filterChange.emit(httpParams);
  }

  /**
   * Build HTTP parameters from form values
   */
  private buildHttpParams(): HttpParams {
    let httpParams: HttpParams = new HttpParams();
    const formValue: ServiceRequestListFilter = this.form.value;

    // Search Term
    if (formValue.searchTerm?.trim()) {
      httpParams = httpParams.append('SearchTerm', formValue.searchTerm.trim());
    }

    // Status (MultiSelect)
    if (formValue.status?.length) {
      formValue.status.forEach((statusItem: SelectItem) => {
        if (statusItem.value !== undefined) {
          httpParams = httpParams.append('Status', statusItem.value);
        }
      });
    }

    // Requester Email
    if (formValue.requester?.trim()) {
      httpParams = httpParams.append('Requester', formValue.requester.trim());
    }

    // Severity (Single Select)
    if (formValue.severity?.value) {
      httpParams = httpParams.append('Severity', formValue.severity.value);
    }

    // Urgency (Single Select)
    if (formValue.urgency?.value) {
      httpParams = httpParams.append('Urgency', formValue.urgency.value);
    }

    // Critical (Single Select)
    if (formValue.critical?.value) {
      httpParams = httpParams.append('Critical', formValue.critical.value);
    }

    return httpParams;
  }

  clearFilter(): void {
    this.form.reset({
      searchTerm: '',
      status: this.initialStatusValues,
      requester: '',
      severity: null,
      urgency: null,
      critical: null,
    });

    this.filterChange.emit(new HttpParams());
  }

  /**
   * Check if the clear button should be disabled
   *
   * @returns boolean
   */
  isClearButtonDisabled(): boolean {
    const formValue: ServiceRequestListFilter = this.form.value;
    return !(
      formValue.searchTerm?.trim() ||
      formValue.status?.length ||
      formValue.requester?.trim() ||
      formValue.severity ||
      formValue.urgency ||
      formValue.critical
    );
  }
}
